import React, { useState } from 'react';
import { Card, Input, Button, Form, message, Typography, Space, Tabs, Tag, List, Avatar, Modal, Empty, Alert } from 'antd';
import { UserOutlined, LoginOutlined, UserAddOutlined, LinkOutlined, DeleteOutlined, CheckCircleOutlined, LockOutlined, SafetyOutlined } from '@ant-design/icons';

const { Title, Text } = Typography;

const API_BASE_URL = '';

const LoginPage = ({ onLogin }) => {
  const [activeTab, setActiveTab] = useState('login');
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();
  
  // 首次登录绑定角色相关状态
  const [needBindCharacter, setNeedBindCharacter] = useState(false);
  const [pendingUser, setPendingUser] = useState(null);
  const [bindCharacterName, setBindCharacterName] = useState('');
  const [bindLoading, setBindLoading] = useState(false);
  
  // 管理员 PIN 验证相关状态
  const [needPinVerify, setNeedPinVerify] = useState(false);
  const [pendingUsername, setPendingUsername] = useState('');
  const [pinValue, setPinValue] = useState('');
  const [pinLoading, setPinLoading] = useState(false);

  // 检查用户是否是管理员
  const checkIsAdmin = async (username) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/${username}/check-admin`);
      if (res.ok) {
        const data = await res.json();
        return data.isAdmin;
      }
    } catch (e) {
      console.error(e);
    }
    return false;
  };

  // 执行实际登录
  const doLogin = async (username, pin = null) => {
    const res = await fetch(`${API_BASE_URL}/api/users/login`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ username: username.trim(), pin })
    });
    const data = await res.json();
    
    if (res.ok) {
      // 管理员登录返回 token，持久化保存（普通用户为 null）
      if (data.token) {
        localStorage.setItem('adminToken', data.token);
      }
      // 首次设置 PIN 的提示
      if (data.pinSet) {
        messageApi.success('PIN 码已设置成功！请牢记您的 PIN 码');
      }
      
      // 检查用户是否已有绑定的角色
      if (!data.user.characters || data.user.characters.length === 0) {
        // 首次登录，需要绑定角色
        setPendingUser(data.user);
        setNeedBindCharacter(true);
        setNeedPinVerify(false);
        messageApi.info('首次登录，请先绑定您的第一个角色');
      } else {
        messageApi.success('登录成功！');
        localStorage.setItem('maplelegends-user', JSON.stringify(data.user));
        onLogin(data.user);
      }
      return true;
    } else {
      messageApi.error(data.detail || '登录失败');
      return false;
    }
  };

  const handleLogin = async (values) => {
    setLoading(true);
    try {
      const username = values.username.trim();
      
      // 先检查是否是管理员
      const isAdmin = await checkIsAdmin(username);
      
      if (isAdmin) {
        // 管理员需要输入 PIN
        setPendingUsername(username);
        setNeedPinVerify(true);
        setPinValue('');
        messageApi.info('管理员登录需要输入 PIN 码');
      } else {
        // 普通用户直接登录
        await doLogin(username);
      }
    } catch (e) {
      messageApi.error('登录失败，请检查网络连接');
    }
    setLoading(false);
  };
  
  // 管理员 PIN 验证登录
  const handlePinLogin = async () => {
    if (!pinValue || pinValue.length !== 4) {
      messageApi.warning('请输入 4 位 PIN 码');
      return;
    }
    
    setPinLoading(true);
    try {
      const success = await doLogin(pendingUsername, pinValue);
      if (!success) {
        setPinValue('');
      }
    } catch (e) {
      messageApi.error('登录失败，请检查网络连接');
    }
    setPinLoading(false);
  };

  // 绑定第一个角色
  const handleBindFirstCharacter = async () => {
    if (!bindCharacterName.trim()) {
      messageApi.warning('请输入角色名');
      return;
    }
    setBindLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/link-character`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          username: pendingUser.username, 
          characterName: bindCharacterName.trim() 
        })
      });
      const data = await res.json();
      if (res.ok) {
        messageApi.success('角色绑定成功！');
        // 重新获取用户信息
        const userRes = await fetch(`${API_BASE_URL}/api/users/${pendingUser.username}`);
        if (userRes.ok) {
          const userData = await userRes.json();
          localStorage.setItem('maplelegends-user', JSON.stringify(userData));
          onLogin(userData, true);  // 新用户首次绑定，跳转到 Rules 页面
        }
      } else {
        messageApi.error(data.detail || '绑定失败');
      }
    } catch (e) {
      messageApi.error('绑定失败，请检查网络连接');
    }
    setBindLoading(false);
  };

  const handleRegister = async (values) => {
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/register`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ username: values.username.trim() })
      });
      const data = await res.json();
      if (res.ok) {
        messageApi.success('注册成功！请登录');
        setActiveTab('login');
      } else {
        messageApi.error(data.detail || '注册失败');
      }
    } catch (e) {
      messageApi.error('注册失败，请检查网络连接');
    }
    setLoading(false);
  };

  const tabItems = [
    {
      key: 'login',
      label: (
        <span>
          <LoginOutlined />
          登录
        </span>
      ),
      children: (
        <Form onFinish={handleLogin} layout="vertical">
          <Form.Item
            name="username"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { pattern: /^[a-zA-Z0-9]{3,20}$/, message: '用户名需要3-20位英文字母或数字' }
            ]}
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="输入用户名" 
              size="large"
              autoComplete="username"
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block size="large">
              登录
            </Button>
          </Form.Item>
        </Form>
      )
    },
    {
      key: 'register',
      label: (
        <span>
          <UserAddOutlined />
          注册
        </span>
      ),
      children: (
        <Form onFinish={handleRegister} layout="vertical">
          <Form.Item
            name="username"
            label="用户名"
            rules={[
              { required: true, message: '请输入用户名' },
              { pattern: /^[a-zA-Z0-9]{3,20}$/, message: '用户名需要3-20位英文字母或数字组合' }
            ]}
            extra="用户名需要3-20位，只能包含大小写英文字母和数字"
          >
            <Input 
              prefix={<UserOutlined />} 
              placeholder="设置用户名" 
              size="large"
              autoComplete="username"
            />
          </Form.Item>
          <Form.Item>
            <Button type="primary" htmlType="submit" loading={loading} block size="large">
              注册
            </Button>
          </Form.Item>
        </Form>
      )
    }
  ];

  // 首次登录绑定角色界面
  if (needBindCharacter && pendingUser) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #ffecd2 0%, #fcb69f 50%, #ffccbc 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20
      }}>
        {contextHolder}
        <Card
          style={{
            width: '100%',
            maxWidth: 420,
            borderRadius: 16,
            boxShadow: '0 10px 40px rgba(255, 107, 149, 0.2)',
            border: '1px solid rgba(255, 133, 162, 0.3)'
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <Title level={2} style={{ margin: 0, color: '#ff6b95' }}>
              🍁 欢迎, {pendingUser.username}!
            </Title>
            <Text type="secondary" style={{ color: '#e07a5f' }}>首次登录需要绑定角色</Text>
          </div>
          
          <Alert
            message="请绑定您的第一个游戏角色"
            description="绑定后您可以在 Boss Run Manager 中登记该角色参与活动，获得的收益将关联到您的账户。"
            type="info"
            showIcon
            style={{ marginBottom: 24 }}
          />
          
          <Form layout="vertical">
            <Form.Item label="角色名称" required>
              <Input 
                prefix={<UserOutlined />}
                placeholder="输入您的角色名..." 
                size="large"
                value={bindCharacterName}
                onChange={e => setBindCharacterName(e.target.value)}
                onPressEnter={handleBindFirstCharacter}
                maxLength={12}
              />
            </Form.Item>
            <Form.Item>
              <Button 
                type="primary" 
                onClick={handleBindFirstCharacter}
                loading={bindLoading}
                block 
                size="large"
                icon={<CheckCircleOutlined />}
              >
                绑定角色并进入系统
              </Button>
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button 
                type="link" 
                block
                onClick={() => {
                  setNeedBindCharacter(false);
                  setPendingUser(null);
                  setBindCharacterName('');
                }}
              >
                返回登录
              </Button>
            </Form.Item>
          </Form>
          
          <Text type="secondary" style={{ fontSize: 12, display: 'block', textAlign: 'center', marginTop: 16 }}>
            注意：每个角色只能绑定到一个账号
          </Text>
        </Card>
      </div>
    );
  }

  // 管理员 PIN 验证界面
  if (needPinVerify) {
    return (
      <div style={{
        minHeight: '100vh',
        background: 'linear-gradient(180deg, #ffecd2 0%, #fcb69f 50%, #ffccbc 100%)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        padding: 20
      }}>
        {contextHolder}
        <Card
          style={{
            width: '100%',
            maxWidth: 400,
            borderRadius: 16,
            boxShadow: '0 10px 40px rgba(255, 107, 149, 0.2)',
            border: '1px solid rgba(255, 133, 162, 0.3)'
          }}
        >
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <SafetyOutlined style={{ fontSize: 48, color: '#faad14', marginBottom: 16 }} />
            <Title level={3} style={{ margin: 0, color: '#ff6b95' }}>
              管理员验证
            </Title>
            <Text type="secondary" style={{ color: '#e07a5f' }}>
              {pendingUsername} 是管理员账号
            </Text>
          </div>
          
          <Alert
            message="首次登录提示"
            description="如果您尚未设置 PIN 码，输入的 PIN 将自动设为您的登录密码，请牢记！"
            type="info"
            showIcon
            style={{ marginBottom: 16 }}
          />
          
          <Form layout="vertical">
            <Form.Item label="请输入 4 位 PIN 码" required>
              <Input.Password
                prefix={<LockOutlined />}
                placeholder="输入 PIN 码"
                size="large"
                maxLength={4}
                value={pinValue}
                onChange={e => {
                  const val = e.target.value.replace(/\D/g, '');
                  setPinValue(val);
                }}
                onPressEnter={handlePinLogin}
                style={{ letterSpacing: 8, textAlign: 'center', fontSize: 20 }}
              />
            </Form.Item>
            <Form.Item>
              <Button 
                type="primary" 
                onClick={handlePinLogin}
                loading={pinLoading}
                block 
                size="large"
                disabled={pinValue.length !== 4}
              >
                验证并登录
              </Button>
            </Form.Item>
            <Form.Item style={{ marginBottom: 0 }}>
              <Button 
                type="link" 
                block
                onClick={() => {
                  setNeedPinVerify(false);
                  setPendingUsername('');
                  setPinValue('');
                }}
              >
                返回登录
              </Button>
            </Form.Item>
          </Form>
        </Card>
      </div>
    );
  }

  return (
    <div style={{
      minHeight: '100vh',
      background: 'linear-gradient(180deg, #ffecd2 0%, #fcb69f 50%, #ffccbc 100%)',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      padding: 20
    }}>
      {contextHolder}
      <Card
        style={{
          width: '100%',
          maxWidth: 400,
          borderRadius: 16,
          boxShadow: '0 10px 40px rgba(255, 107, 149, 0.2)',
          border: '1px solid rgba(255, 133, 162, 0.3)'
        }}
      >
        <div style={{ textAlign: 'center', marginBottom: 24 }}>
          <Title level={2} style={{ margin: 0, color: '#ff6b95' }}>
            🍁 Maple
          </Title>
          <Text type="secondary" style={{ color: '#e07a5f' }}>Bonus Bank</Text>
        </div>
        <Tabs 
          activeKey={activeTab} 
          onChange={setActiveTab}
          items={tabItems}
          centered
        />
      </Card>
    </div>
  );
};

// 用户信息管理页面组件
export const UserProfileModal = ({ visible, onClose, user, onUserUpdate }) => {
  const [linkCharacterName, setLinkCharacterName] = useState('');
  const [loading, setLoading] = useState(false);
  const [messageApi, contextHolder] = message.useMessage();

  const handleLinkCharacter = async () => {
    if (!linkCharacterName.trim()) {
      messageApi.warning('请输入角色名');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/link-character`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ 
          username: user.username, 
          characterName: linkCharacterName.trim() 
        })
      });
      const data = await res.json();
      if (res.ok) {
        messageApi.success(data.message);
        setLinkCharacterName('');
        // 刷新用户信息
        const userRes = await fetch(`${API_BASE_URL}/api/users/${user.username}`);
        if (userRes.ok) {
          const userData = await userRes.json();
          onUserUpdate(userData);
          localStorage.setItem('maplelegends-user', JSON.stringify(userData));
        }
      } else {
        messageApi.error(data.detail || '关联失败');
      }
    } catch (e) {
      messageApi.error('关联失败，请检查网络连接');
    }
    setLoading(false);
  };

  const handleUnlinkCharacter = async (characterName) => {
    try {
      const res = await fetch(`${API_BASE_URL}/api/users/${user.username}/characters/${characterName}`, {
        method: 'DELETE'
      });
      const data = await res.json();
      if (res.ok) {
        messageApi.success(data.message);
        // 刷新用户信息
        const userRes = await fetch(`${API_BASE_URL}/api/users/${user.username}`);
        if (userRes.ok) {
          const userData = await userRes.json();
          onUserUpdate(userData);
          localStorage.setItem('maplelegends-user', JSON.stringify(userData));
        }
      } else {
        messageApi.error(data.detail || '解除关联失败');
      }
    } catch (e) {
      messageApi.error('操作失败');
    }
  };

  return (
    <Modal 
      title={<><UserOutlined /> 用户信息</>}
      open={visible} 
      onCancel={onClose}
      footer={null}
      width={500}
    >
      {contextHolder}
      <div style={{ marginBottom: 24 }}>
        <Text strong>用户名：</Text>
        <Tag color="blue" style={{ marginLeft: 8, fontSize: 14 }}>{user?.username}</Tag>
      </div>
      
      <div style={{ marginBottom: 16 }}>
        <Text strong>关联角色 ({user?.characters?.length || 0})</Text>
      </div>
      
      {user?.characters?.length > 0 ? (
        <List
          size="small"
          dataSource={user.characters}
          renderItem={char => (
            <List.Item
              actions={[
                <Button 
                  type="text" 
                  danger 
                  icon={<DeleteOutlined />}
                  onClick={() => handleUnlinkCharacter(char)}
                >
                  解除
                </Button>
              ]}
            >
              <List.Item.Meta
                avatar={
                  <Avatar 
                    src={`${API_BASE_URL}/api/getavatar?name=${char}`}
                    size={40}
                  />
                }
                title={char}
              />
            </List.Item>
          )}
          style={{ marginBottom: 16, maxHeight: 300, overflow: 'auto' }}
        />
      ) : (
        <Empty description="暂无关联角色" style={{ marginBottom: 16 }} />
      )}
      
      <div style={{ borderTop: '1px solid #f0f0f0', paddingTop: 16 }}>
        <Text strong style={{ display: 'block', marginBottom: 8 }}>
          <LinkOutlined /> 关联新角色
        </Text>
        <Space.Compact style={{ width: '100%' }}>
          <Input 
            placeholder="输入角色名" 
            value={linkCharacterName}
            onChange={e => setLinkCharacterName(e.target.value)}
            onPressEnter={handleLinkCharacter}
          />
          <Button 
            type="primary" 
            onClick={handleLinkCharacter}
            loading={loading}
          >
            关联
          </Button>
        </Space.Compact>
        <Text type="secondary" style={{ fontSize: 12, display: 'block', marginTop: 4 }}>
          注意：每个角色只能关联到一个账号，关联后其他用户无法再次关联该角色
        </Text>
      </div>
    </Modal>
  );
};

export default LoginPage;
